import type {
  IPriceProductDraft,
  IPriceProductDraftChange,
  IPriceProductDraftPreview,
  IPriceProductDraftsChange,
} from '../types';

import { PRICE_PRODUCT_FIELD, PRICE_PRODUCT_INPUT_PATTERN } from '../constants';
import { findVariantByOrigin, useCampaignContext } from '~/modules/campaign';
import { mergePriceProductDrafts, toPriceProductDrafts } from '../utils';
import { useCallback, useMemo } from 'react';

const EMPTY_DRAFT: IPriceProductDraft = {
  [PRICE_PRODUCT_FIELD.PRICE]: '',
  [PRICE_PRODUCT_FIELD.COMPARE_PRICE]: '',
};

export interface IUsePriceProductDraftResult {
  getDraft: (baseID: string) => IPriceProductDraft;
  /** True once any variant has a Price typed — readable before the variants themselves are known. */
  hasAnyDraftPrice: boolean;
  handleChangeDraft: (change: IPriceProductDraftChange) => void;
  /** One amount down several variants — what a product row's own field writes. */
  handleChangeDrafts: (change: IPriceProductDraftsChange) => void;
  /** Commits a quick-fill preview, which carries a different amount per variant. */
  handleApplyDraftPreview: (preview: IPriceProductDraftPreview) => void;
}

/**
 * Keyed by variant baseID — a product never holds an amount of its own, only its variants do. Held on
 * the challenger's `productVariants` in the campaign, so the campaign's own diff, save and discard
 * cover the typed amounts like any other campaign change.
 */
export const usePriceProductDraft = (): IUsePriceProductDraftResult => {
  const { campaign, handleSetCampaignData } = useCampaignContext();
  const challengerVariant = findVariantByOrigin(campaign, false);
  const productVariants = challengerVariant?.productVariants;

  const draftByBaseID = useMemo(() => toPriceProductDrafts(productVariants), [productVariants]);

  const getDraft = useCallback(
    (baseID: string) => draftByBaseID[baseID] ?? EMPTY_DRAFT,
    [draftByBaseID],
  );

  const hasAnyDraftPrice = useMemo(
    () => Object.values(draftByBaseID).some((draft) => !!draft[PRICE_PRODUCT_FIELD.PRICE]),
    [draftByBaseID],
  );

  const handleApplyDraftPreview = useCallback(
    (drafts: IPriceProductDraftPreview) => {
      if (!campaign || !challengerVariant) return;

      const nextProductVariants = mergePriceProductDrafts({
        productVariants,
        variantID: challengerVariant.id,
        drafts,
      });
      const variants = campaign.variants?.map((variant) =>
        variant?.id === challengerVariant.id
          ? { ...variant, productVariants: nextProductVariants }
          : variant,
      );

      handleSetCampaignData({ ...campaign, variants });
    },
    [campaign, challengerVariant, productVariants, handleSetCampaignData],
  );

  const handleChangeDrafts = useCallback(
    ({ baseIDs, field, value }: IPriceProductDraftsChange) => {
      // A rejected keystroke leaves the field as it was rather than clearing it — the merchant sees the
      // character simply not land, which is what every other amount field in the admin does.
      if (!PRICE_PRODUCT_INPUT_PATTERN.test(value)) return;

      handleApplyDraftPreview(
        Object.fromEntries(baseIDs.map((baseID) => [baseID, { [field]: value }])),
      );
    },
    [handleApplyDraftPreview],
  );

  const handleChangeDraft = useCallback(
    ({ baseID, field, value }: IPriceProductDraftChange) =>
      handleChangeDrafts({ baseIDs: [baseID], field, value }),
    [handleChangeDrafts],
  );

  return {
    getDraft,
    hasAnyDraftPrice,
    handleChangeDraft,
    handleChangeDrafts,
    handleApplyDraftPreview,
  };
};
